import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '../ThemeContext'
import { apiURL } from '../utilities/URLs'
import { refreshAccessToken } from '../utilities/extensions'
import LoadingBar from 'react-top-loading-bar'

import leftArrow from '../assets/images/craig_arrow_left.png'
import rightArrow from '../assets/images/craig_arrow_right.png'
import 'tailwindcss/tailwind.css'
import ExportModal from '../utilities/ExportModal'
import { affiliateData, company } from '../data/AffiliateData'

export default function Leads() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isDarkMode } = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const [fetchingLeads, setFetchingLeads] = useState(true)
  const [leads, setLeads] = useState([])
  const [page, setPage] = useState(searchParams.get('page') ? Number(searchParams.get('page')) : 1)
  const [pageSize, setPageSize] = useState(searchParams.get('pageSize') ? Number(searchParams.get('pageSize')) : 100)
  const [isSubAffiliates, setIsSubAffiliates] = useState(searchParams.get('isSubAffiliates') ? Boolean(Number(searchParams.get('isSubAffiliates'))) : null)
  const [activeTab, setActiveTab] = useState('left')

  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('searchQuery') ? decodeURIComponent(searchParams.get('searchQuery')) : '')
  const [leadStageId, setLeadStageId] = useState(searchParams.get('leadStageId') ? searchParams.get('leadStageId') : '')
  const [columnFilter, setColumnFilter] = useState(searchParams.get('columnFilter') ? searchParams.get('columnFilter') : '')
  const [columnSortOrder, setColumnSortOrder] = useState(searchParams.get('columnSortOrder') ? searchParams.get('columnSortOrder') : '')

  const [canGetNextLeads, setCanGetNextLeads] = useState(true)

  const [showExport, setShowExport] = useState(false)

  const loadingBarRef = useRef(null)

  const handleGetLeads = async () => {
    setCanGetNextLeads(false)
    loadingBarRef.current.continuousStart()

    const form = new FormData()
    form.append('sheetInfo', JSON.stringify({
      page: page,
      pageSize: pageSize,
      affiliateId: affiliateData.account.accountId,
      affiliateIds: isSubAffiliates ? affiliateData.subAffiliates.map(a => a.subAffiliateId) : [affiliateData.account.accountId],
      leadStageId: leadStageId,
      columnFilter: columnFilter,
      columnSortOrder: columnSortOrder,
      searchQuery: searchQuery
    }))

    const leadsResponse = await fetch(`${apiURL}/api/getLeads`, {
      method: 'POST',
      headers: {
        'Authorization': affiliateData.accessToken
      },
      body: form
    })
    const leadsData = await leadsResponse.json()
    setCanGetNextLeads(true)
    setFetchingLeads(false)
    loadingBarRef.current.complete()

    if (leadsData.response.error) {
      if (leadsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? handleGetLeads() : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(leadsData.response.error)
    } else {
      setLeads(leadsData.response.leadsSheet)
      setHasMore(leadsData.response.hasMore)
    }
  }

  useEffect(() => {
    if (window.innerWidth > 640)
      setActiveTab('both')

    document.title = `${company.companyName} | ${searchParams.get('isSubAffiliates') ? 'Sub Affiliate Leads' : 'Leads'}`
  }, [])

  useEffect(() => {
    searchParams.set('page', page)
    searchParams.set('pageSize', pageSize)
    if(isSubAffiliates) searchParams.set('isSubAffiliates', Number(isSubAffiliates))
    if(leadStageId) searchParams.set('leadStageId', leadStageId)
    if(columnFilter) searchParams.set('columnFilter', columnFilter)
    if(columnSortOrder) searchParams.set('columnSortOrder', columnSortOrder)
    if(searchQuery) searchParams.set('searchQuery', encodeURIComponent(searchQuery))

    setSearchParams(searchParams)
    handleGetLeads()
  }, [page, pageSize, isSubAffiliates, leadStageId, columnFilter, columnSortOrder])

  useEffect(() => {
    setPage(searchParams.get('page') ? Number(searchParams.get('page')) : 1)
    setPageSize(searchParams.get('pageSize') ? Number(searchParams.get('pageSize')) : 100)
    setIsSubAffiliates(searchParams.get('isSubAffiliates') ? Boolean(Number(searchParams.get('isSubAffiliates'))) : null)
  }, [location.search])

  return (
    <div className={`w-full h-screen ${isDarkMode && 'bg-[#1a1d21]'}`}>
      <LoadingBar color={company.companyPrimaryColor} ref={loadingBarRef} />
      <div className={`flex items-center justify-center py-4 font-mont ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className='relative flex items-center lg:w-1/3 w-4/5'>
          <input
            type='text'
            placeholder='Search Leads...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if(e.key === 'Enter') {
                searchParams.set('searchQuery', encodeURIComponent(searchQuery))
                setSearchParams(searchParams)
                handleGetLeads()
              }
            }}
            className={`w-full p-2 mb-4 text-gray-700 border rounded-l-lg focus:outline-none focus:shadow-outline ${isDarkMode && 'bg-gray-800 text-white'}`}
          />
          <button
            onClick={() => {
              searchParams.set('searchQuery', encodeURIComponent(searchQuery))
              setSearchParams(searchParams)
              handleGetLeads()
            }}
            className={`p-2 mb-4 text-white rounded-r-lg focus:outline-none border ${searchQuery ? 'bg-blue-500 hover:bg-blue-700' : 'bg-gray-500 cursor-not-allowed'}`}
            disabled={!searchQuery}
          >
            Search
          </button>
        </div>
      </div>
      <div className={`w-full flex items-center justify-center py-4 font-mont gap-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className='relative w-full flex flex-row justify-center items-center gap-4'>
          <button onClick={() => setShowExport(true)} className='px-4 py-2 text-sm font-medium text-white rounded-lg bg-primaryColor hover:bg-primaryDarkColor duration-200'>
            Export To CSV
          </button>
          {isSubAffiliates && (
            <button
              className={`px-4 py-2 text-sm font-medium bg-secondaryColor hover:bg-secondaryDarkColor text-white rounded-lg`}
              onClick={() => navigate('/affiliate/subAffiliateList')}
            >
              Sub Affiliate List
            </button>
          )}
          <select 
            value={pageSize} 
            onChange={(e) => setPageSize(Number(e.target.value))} 
            className={`absolute right-10 border rounded-lg px-4 py-2 shadow-sm transition-colors duration-200 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
          >
            <option value={25}>25 leads per page</option>
            <option value={50}>50 leads per page</option>
            <option value={75}>75 leads per page</option>
            <option value={100}>100 leads per page</option>
          </select>
        </div>
      </div>
      <div className={`lg:hidden flex justify-center font-mont ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <button
          className={`px-4 py-2 text-sm font-semibold rounded-l-lg ${activeTab === 'left' ? 'bg-primaryColor text-white' : `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}`}
          onClick={() => setActiveTab('left')}
        >
          Left
        </button>
        <button
          className={`px-4 py-2 text-sm font-semibold rounded-r-lg ${activeTab === 'right' ? 'bg-secondaryColor text-white' : `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}`}
          onClick={() => setActiveTab('right')}
        >
          Right
        </button>
      </div>
      {leads.length > 0 && (
        <div className={`flex items-center justify-center py-4 font-mont ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <button
            onClick={() => setPage(page-1)}
            className={`mr-2 ${page === 1 || !canGetNextLeads ? 'pointer-events-none opacity-50' : ''}`}
            disabled={!canGetNextLeads || page === 1}
          >
            <img src={leftArrow} alt='page left' className='w-8 h-8' />
          </button>
          <span className={`${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
            {page > 1 ? ((page*pageSize) - (pageSize - 1)) : 1}-{page*pageSize}
          </span>
          <button
            onClick={() => setPage(page+1)}
            className={`ml-2 ${ !canGetNextLeads || !hasMore ? 'pointer-events-none opacity-50' : ''}`}
            disabled={!canGetNextLeads || !hasMore}
          >
            <img src={rightArrow} alt='page right' className='w-8 h-8' />
          </button>
        </div>
      )}
      <div>
        <table className='min-w-full leading-normal font-mont text-center'>
          <thead>
            <tr>
              {activeTab !== 'both' ? (
                activeTab === 'left' ? (
                  <>
                    <th className={`pl-5 py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('NAME'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Name</p>
                        <div onClick={() => { setColumnFilter('NAME'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                    <th className={`py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('AMOUNT'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Amount</p>
                        <div onClick={() => { setColumnFilter('AMOUNT'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                    <th className={`py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('STAGE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Stage</p>
                        <div onClick={() => { setColumnFilter('STAGE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                  </>
                ) : activeTab === 'right' ? (
                  <>
                    <th className={`py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('SOURCE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Source</p>
                        <div onClick={() => { setColumnFilter('SOURCE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                    <th className={`py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('MODIFIED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Modified</p>
                        <div onClick={() => { setColumnFilter('MODIFIED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                    <th className={`py-3 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      <div className='flex items-center justify-center'>
                        <p onClick={() => { setColumnFilter('CREATED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Created</p>
                        <div onClick={() => { setColumnFilter('CREATED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                          <span>▲</span>
                          <span className='-mt-1.5'>▼</span>
                        </div>
                      </div>
                    </th>
                  </>
                ) : null
              ) : (
                <>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('NAME'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Name</p>
                      <div onClick={() => { setColumnFilter('NAME'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('COMPANY'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Company</p>
                      <div onClick={() => { setColumnFilter('COMPANY'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('AMOUNT'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Amount</p>
                      <div onClick={() => { setColumnFilter('AMOUNT'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p onClick={() => { setColumnFilter('DEADLINE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='cursor-pointer'>Deadline</p>
                      <div onClick={() => { setColumnFilter('DEADLINE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('QUALIFIES'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Qualifies</p>
                      <div onClick={() => { setColumnFilter('QUALIFIES'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('STAGE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Stage</p>
                      <div onClick={() => { setColumnFilter('STAGE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('SOURCE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Source</p>
                      <div onClick={() => { setColumnFilter('SOURCE'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('MODIFIED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Modified</p>
                      <div onClick={() => { setColumnFilter('MODIFIED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                  <th className={`w-1/9 border-b-2 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'bg-gray-800 text-white' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                    <div className='flex items-center justify-center'>
                      <p className='cursor-pointer' onClick={() => { setColumnFilter('CREATED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}}>Created</p>
                      <div onClick={() => { setColumnFilter('CREATED'); setColumnSortOrder((columnSortOrder && columnSortOrder === 'ASC') ? 'DESC' : 'ASC')}} className='flex flex-col items-center ml-1 cursor-pointer'>
                        <span>▲</span>
                        <span className='-mt-1.5'>▼</span>
                      </div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          {activeTab !== 'both' ? (
            <tbody className='relative'>
              {leads.length === 0 ? (
                fetchingLeads ? (
                  <div className='absolute flex flex-row items-center justify-center gap-2 mt-5 w-full col-span-2 font-mont'>
                    <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
                    <p className={`text-md text-center ${isDarkMode && 'text-white'}`}>Fetching Leads...</p>
                  </div>
                ) : (
                  <div className={`absolute flex flex-row items-center justify-center gap-2 mt-5 w-full col-span-2 font-mont ${isDarkMode && 'text-white'}`}>No Leads Found.</div>
                )
              ) : (
                leads.map((lead, index) => (
                  <tr onClick={() => navigate(`/affiliate/leads/${lead.leadId}`)} key={index} className={`${index % 2 === 0 ? `${isDarkMode ? 'bg-[#1a1d21]' : 'bg-white'}` : `${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}`}>
                    {activeTab === 'left' && (
                      <>
                        <td className={`w-1/3 pl-2 py-5 border-b border-gray-200 text-[13.5px] ${isDarkMode && 'text-white'}`}>{lead.contactName}</td>
                        <td className={`w-1/3 border-b border-gray-200 text-[13.5px] pr-3 ${isDarkMode && 'text-white'}`}>${Number(lead.leadAmount).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: Number(lead.leadAmount) % 1 !== 0 ? 2 : 0 })}</td>
                        <td className={`w-1/3 pr-2 border-b border-gray-200 text-[13.5px] ${isDarkMode && 'text-white'}`}>{lead.leadStageName}</td>
                      </>
                    )}
                    {activeTab === 'right' && (
                      <>
                        <td className={`w-1/3 pl-2 py-5 border-b border-gray-200 text-[13.5px] pr-4 ${isDarkMode && 'text-white'}`}>{lead.leadSource}</td>
                        <td className={`w-1/3 border-b border-gray-200 text-[13.5px] ${isDarkMode && 'text-white'}`}>
                          <div className='flex flex-col pr-1'>
                            <div>{new Date(lead.leadModifiedAt).toLocaleDateString()}</div>
                            <div>{new Date(lead.leadModifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </td>
                        <td className={`w-1/3 pr-2 border-b border-gray-200 text-[13.5px] ${isDarkMode && 'text-white'}`}>
                          <div className='flex flex-col'>
                            <div>{new Date(lead.leadCreatedAt).toLocaleDateString()}</div>
                            <div>{new Date(lead.leadCreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          ) : (
            <tbody className='relative'>
              {leads.length === 0 ? (
                fetchingLeads ? (
                  <div className='absolute flex flex-row items-center justify-center gap-2 mt-5 w-full col-span-2 font-mont'>
                    <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
                    <p className={`text-md text-center ${isDarkMode && 'text-white'}`}>Fetching Leads...</p>
                  </div>
                ) : (
                  <div className={`absolute flex flex-row items-center justify-center gap-2 mt-5 w-full col-span-2 font-mont ${isDarkMode && 'text-white'}`}>No Leads Found.</div>
                )
              ) : (
                leads.map((lead, index) => (
                  <tr onClick={() => navigate(`/affiliate/leads/${lead.leadId}`)} key={index} className={`${index % 2 === 0 ? `${isDarkMode ? 'bg-[#1a1d21] hover:bg-gray-700' : 'bg-white hover:bg-gray-300'}` : `${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`} cursor-pointer`}>
                    <td className={`w-1/9 py-5 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>{lead.contactName}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>{lead.companyName}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>${Number(lead.leadAmount).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: Number(lead.leadAmount) % 1 !== 0 ? 2 : 0 })}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>
                      {(() => {
                        if (!lead.filingDate) return 'N/A'

                        const originalDate = new Date(lead.filingDate)
                        if (isNaN(originalDate.getTime())) return 'N/A'

                        const lowerBound = new Date('2022-04-18')
                        const upperBound = new Date('2022-10-15')
                        const currentDate = new Date()
                        let newDate

                        if (originalDate < lowerBound)
                          newDate = new Date('2025-04-18')
                        else if (originalDate > upperBound)
                          newDate = new Date('2025-10-15')
                        else {
                          const [, month, day] = lead.filingDate.split('-')
                          newDate = new Date(`2025-${month}-${day}`)
                        }
                        newDate = new Date(newDate.getTime() + 24 * 60 * 60 * 1000)
                        const daysDiff = Math.max(0, Math.ceil((newDate - currentDate) / (1000 * 60 * 60 * 24)))
                        return `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`
                      })()}
                    </td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>{Boolean(lead.qualifies) ? 'Yes' : 'No'}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>{lead.leadStageName}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>{lead.leadSource}</td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>
                      <div className='flex flex-col'>
                        <div>{new Date(lead.leadModifiedAt).toLocaleDateString()}</div>
                        <div>{new Date(lead.leadModifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </td>
                    <td className={`w-1/9 border-b border-gray-200 text-sm ${isDarkMode && 'text-white'}`}>
                      <div className='flex flex-col'>
                        <div>{new Date(lead.leadCreatedAt).toLocaleDateString()}</div>
                        <div>{new Date(lead.leadCreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          )}
        </table>
      </div>
      {leads.length > 0 && (
        <div className={`flex items-center justify-center py-4 font-mont ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <button
            onClick={() => setPage(page-1)}
            className={`mr-2 ${page === 1 || !canGetNextLeads ? 'pointer-events-none opacity-50' : ''}`}
            disabled={!canGetNextLeads || page === 1}
          >
            <img src={leftArrow} alt='page left' className='w-8 h-8' />
          </button>
          <span className={`${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
            {page > 1 ? ((page*pageSize) - (pageSize - 1)) : 1}-{page*pageSize}
          </span>
          <button
            onClick={() => setPage(page+1)}
            className={`ml-2 ${ !canGetNextLeads || !hasMore ? 'pointer-events-none opacity-50' : ''}`}
            disabled={!canGetNextLeads || !hasMore}
          >
            <img src={rightArrow} alt='page right' className='w-8 h-8' />
          </button>
        </div>
      )}
      {showExport && (
        <ExportModal
          setShowExport={setShowExport}
          leadStageId={leadStageId}
        />
      )}
    </div>
  )
}